import { useCallback, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';

type StickyFooterProps = {
  targetId: string;
  mappedTargetId: string;
  enabled: boolean;
  jumpLinkId: string;
};

export const stickyFooterHideStyle = {
  position: 'fixed',
  left: '0',
  right: '0',
  bottom: '-100%',
  zIndex: '1',
};

export const useStickyFooter = (
  stickyFooter: StickyFooterProps,
  jumpLinkId = ''
) => {
  const { ref, inView } = useInView();
  const stickyFooterTargetId = stickyFooter?.targetId;
  const stickyFooterMappedTargetId = stickyFooter?.mappedTargetId;
  const stickyFooterEnabled = stickyFooter?.enabled;

  const handleScroll = useCallback(() => {
    if (!stickyFooterEnabled) {
      return;
    }

    if (stickyFooterTargetId || jumpLinkId) {
      const targetElement = document.getElementById(
        stickyFooterTargetId || jumpLinkId
      );

      if (!targetElement) {
        return;
      }

      if (!stickyFooterEnabled) {
        targetElement.style.position = 'unset';
        return;
      }

      targetElement.style.position = 'fixed';
      targetElement.style.left = '0';
      targetElement.style.right = '0';
      targetElement.style.zIndex = '1';
      targetElement.style.transition = 'all 0.2s ease-in-out';

      if (inView) {
        targetElement.style.bottom = '-100%';
        return;
      }
      targetElement.style.bottom = '0';
    }
  }, [stickyFooterEnabled, stickyFooterTargetId, jumpLinkId, inView]);

  // cleanup the scroll event listener
  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  // Reset the sticky footer when the sticky footer is disabled
  useEffect(() => {
    if (stickyFooterTargetId) {
      const targetElement = document.getElementById(stickyFooterTargetId);
      if (targetElement && !stickyFooterEnabled) {
        targetElement.style.position = 'unset';
        targetElement.style.left = 'unset';
        targetElement.style.right = 'unset';
        targetElement.style.zIndex = 'unset';
      }
    }
  }, [stickyFooterEnabled, stickyFooterTargetId]);

  return { ref, stickyFooterMappedTargetId };
};
